// SuggestionsPanel.tsx
//
// Lists pending tracked-change suggestions with per-suggestion + bulk
// accept/reject. Rendered as a collapsible section above the editor (the right
// rail is the comments panel), so suggestions and comments coexist.
import { useState } from "react";
import { Check, X, ChevronDown, ChevronUp, GitPullRequestArrow } from "lucide-react";
import type { Suggestion } from "./suggestionOps";

interface SuggestionsPanelProps {
  suggestions: Suggestion[];
  /** Edit-capable users may accept/reject; others see a read-only list. */
  canAcceptReject?: boolean;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
}

const initials = (name: string) =>
  (name || "?")
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

export const SuggestionsPanel = ({
  suggestions,
  canAcceptReject = false,
  onAccept,
  onReject,
  onAcceptAll,
  onRejectAll,
}: SuggestionsPanelProps) => {
  const [expanded, setExpanded] = useState(true);
  if (!suggestions.length) return null;

  return (
    <div className="border border-amber-200 rounded-md bg-amber-50/40 overflow-hidden mb-2">
      <div className="flex items-center px-3 py-2 select-none">
        <button
          className="flex items-center gap-1.5 bg-transparent border-0 cursor-pointer p-0 text-[11px] font-semibold text-amber-700"
          onClick={() => setExpanded((v) => !v)}
        >
          <GitPullRequestArrow className="h-3 w-3" />
          Suggestions ({suggestions.length})
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>
        <div className="flex-1" />
        {canAcceptReject && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={onAcceptAll}
              className="text-[10px] font-semibold text-emerald-700 bg-transparent border-0 cursor-pointer hover:underline"
            >
              Accept all
            </button>
            <span className="text-amber-300">·</span>
            <button
              onClick={onRejectAll}
              className="text-[10px] font-semibold text-red-600 bg-transparent border-0 cursor-pointer hover:underline"
            >
              Reject all
            </button>
          </div>
        )}
      </div>
      {expanded && (
        <div className="px-2 pb-2 space-y-1.5 border-t border-amber-100 pt-2">
          {suggestions.map((s) => (
            <div
              key={s.id}
              data-suggestion-row-id={s.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded bg-white border border-amber-100"
            >
              <span
                className="h-5 w-5 shrink-0 rounded-full text-white text-[9px] font-bold flex items-center justify-center"
                style={{ background: s.color }}
                title={s.authorName}
              >
                {initials(s.authorName)}
              </span>
              <span className="text-[11px] text-foreground/80 min-w-0 flex-1 truncate">
                <span className="font-semibold">
                  {s.type === "insert" ? "Insert" : "Delete"}:
                </span>{" "}
                <span className={s.type === "delete" ? "line-through opacity-70" : "underline"}>
                  {s.text || "(empty)"}
                </span>
              </span>
              {canAcceptReject && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => onAccept(s.id)}
                    title="Accept"
                    className="p-1 rounded hover:bg-emerald-50 bg-transparent border-0 cursor-pointer text-emerald-600"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => onReject(s.id)}
                    title="Reject"
                    className="p-1 rounded hover:bg-red-50 bg-transparent border-0 cursor-pointer text-red-500"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
